import { CurrencyConversion, CurrencySymbol } from '@/src/types/currency'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { extractJson } from '@/src/utils/currency/extractJson'

// Dynamic baseUrl: use rewrite in development, direct URL in production
const getBaseUrl = () => {
    const isDevelopment = process.env.NODE_ENV === 'development'
    return isDevelopment ? '/json/cleartrip-prod/' : 'https://ui.cltp.co/json/cleartrip-prod/'
}

const staggeredBaseQueryWithRetry = retry(
    fetchBaseQuery({
        baseUrl: getBaseUrl(),
        prepareHeaders: headers => {
            headers.set('Accept', 'application/json')
            headers.set('Content-Type', 'application/json')
            headers.set('Cache-Control', 'no-cache')
            return headers
        },
        // Handle malformed JSON responses
        responseHandler: async response => {
            const text = await response.text()
            return text
        }
    }),
    {
        maxRetries: 2
    }
)

export const currencyApi = createApi({
    reducerPath: 'currencyApi',
    baseQuery: staggeredBaseQueryWithRetry,
    tagTypes: ['CurrencyData'],
    endpoints: builder => ({
        // Combined query for both APIs - ensures both resolve together with RTK's built-in retry
        getCurrencyData: builder.query<
            {
                symbols: CurrencySymbol[]
                conversions: CurrencyConversion[]
            },
            void
        >({
            queryFn: async (arg, api, extraOptions, baseQuery) => {
                const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')

                // Fetch both APIs in parallel - baseQuery already has retry built-in
                const [symbolsResult, conversionsResult] = await Promise.all([
                    baseQuery({
                        url: 'currency-symbol.json',
                        params: { ts: timestamp }
                    }),
                    baseQuery({
                        url: 'currency-conversion.json',
                        params: { ts: timestamp }
                    })
                ])

                // Check for errors - return the error as-is to maintain RTK Query error format
                if (symbolsResult.error) {
                    return symbolsResult
                }
                if (conversionsResult.error) {
                    return conversionsResult
                }

                // Extract and transform malformed JSON responses
                const symbolsData = extractJson(symbolsResult.data as string)
                const conversionsData = extractJson(conversionsResult.data as string)

                // Validate and transform data
                const symbols: CurrencySymbol[] = Array.isArray(symbolsData)
                    ? symbolsData.map((item: any) => ({
                          code: item.code,
                          name: item.name,
                          symbol: item.symbol
                      }))
                    : []

                const conversions: CurrencyConversion[] = Array.isArray(conversionsData)
                    ? conversionsData.map((item: any) => ({
                          from: item.from,
                          to: item.to,
                          rate: Number(item.rate),
                          buffer: Number(item.buffer)
                      }))
                    : []

                // Validate we got valid data
                if (symbols.length === 0 || conversions.length === 0) {
                    console.error('Currency APIs returned empty or invalid data')
                    return {
                        error: {
                            status: 'PARSING_ERROR',
                            data: 'Invalid currency data received',
                            error: 'Currency APIs returned empty or malformed data'
                        } as any
                    }
                }

                return {
                    data: {
                        symbols,
                        conversions
                    }
                }
            },
            providesTags: ['CurrencyData']
        })
    })
})

// Export hooks for usage in components
export const { useGetCurrencyDataQuery, useLazyGetCurrencyDataQuery } = currencyApi

// Export the API slice for store configuration
export default currencyApi

// Export types for use in other files
export type { CurrencySymbol, CurrencyConversion }
